import React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import { PermitStatusBadge } from "@app.automotus.io/components/permits/PermitStatusBadge";
import type { PermitSummary } from "common/types/permits";

export interface PermitsListPageContentProps {
  permits: PermitSummary[];
  onPermitClick: (permitId: string) => void;
  onApplyClick: () => void;
}

function formatDate(isoString: string | null): string {
  if (!isoString) {
    return "N/A";
  }
  return new Date(isoString).toLocaleDateString();
}

export const PermitsListPageContent: React.FC<PermitsListPageContentProps> = ({
  permits,
  onPermitClick,
  onApplyClick,
}) => {
  return (
    <Box sx={{ maxWidth: "lg", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" fontWeight={600}>
            Permits
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and manage your parking permits
          </Typography>
        </Box>
        {permits.length > 0 && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={onApplyClick}>
            Apply for a Permit
          </Button>
        )}
      </Box>

      {/* Empty State */}
      {permits.length === 0 ? (
        <Box
          sx={{
            border: 1,
            borderStyle: "dashed",
            borderColor: "divider",
            borderRadius: 2,
            bgcolor: "action.hover",
            py: 8,
            textAlign: "center",
            paddingLeft: 3,
            paddingRight: 3,
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No permits yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Apply for a permit to get started with parking in your area.
          </Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={onApplyClick}>
            Apply for a Permit
          </Button>
        </Box>
      ) : (
        /* Permits Table */
        <Paper variant="outlined">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Permit Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Vehicles</TableCell>
                  <TableCell>Expiry Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permits.map((permit) => {
                  const showApplicationStatus = permit.applicationStatus !== "approved";

                  return (
                    <TableRow key={permit.id} hover onClick={() => onPermitClick(permit.id)} sx={{ cursor: "pointer" }}>
                      <TableCell>{permit.permitType.name}</TableCell>
                      <TableCell>
                        {showApplicationStatus ? (
                          <PermitStatusBadge type="application" status={permit.applicationStatus} />
                        ) : (
                          <PermitStatusBadge type="permit" status={permit.status} />
                        )}
                      </TableCell>
                      <TableCell align="center">{permit.vehicleCount}</TableCell>
                      <TableCell>{formatDate(permit.validTo)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default PermitsListPageContent;
